import { auth } from "../../typings/auth";
import { STATE } from "../../thor.const";
import { SaleEditController } from "./sale-edit.controller";
import { Enums, Events } from "../../thor.enums";
import { FileAccept } from "../../typings/common";

const checkSaleLive = [
    "$state",
    "$timeout",
    "saleEditModel",
    ($state: ng.ui.IStateService, $timeout: ng.ITimeoutService, saleEditModel) =>
        $timeout(() => {
            if (!saleEditModel.isLiveSale()) {
                $state.go(STATE.ERROR["403"]);
            }
        })
];

export const setIframeEvents = (
    $log: ng.ILogService,
    $rootScope: ng.IRootScopeService,
    $window: ng.IWindowService,
    saleEditModel
) => {
    const postMessageEventHandler = ({
        data: { saleId, options }
    }: {
        data: {
            saleId: number;
            options: {
                fetchAll?: boolean;
                fetchSale?: boolean;
                fetchSaleParameters?: boolean;
                hideLayout?: boolean;
                importFile?: {
                    file: File | FileList;
                    fileType: FileAccept;
                };
            };
        };
    }) => {
        if (saleId === parseInt(saleEditModel.id, 10)) {
            $log.info("postMessage received for sale", saleId);
            if (options.fetchSaleParameters || options.fetchSale) {
                saleEditModel.fetchSaleParameters(options);
            } else if (options.fetchAll) {
                saleEditModel.fetchAll();
            }
            if (options.hideLayout !== undefined) {
                const elements: NodeListOf<HTMLElement> = document.querySelectorAll(
                    ".sale-edit-header, add-item, add-online-item"
                );
                for (let i = 0; i < elements.length; ++i) {
                    elements[i].style.display = options.hideLayout ? "none" : "block";
                }
            }
            if (options.importFile) {
                $log.info("postMessage brodcasted", saleId);
                $rootScope.$broadcast(Events.ImportFile, options.importFile);
            }
        }
    };
    $window.removeEventListener("message", postMessageEventHandler);
    $window.addEventListener("message", postMessageEventHandler);
};

export class SaleEditConfiguration {
    public static $inject: string[] = ["$stateProvider"];

    constructor($stateProvider: angular.ui.IStateProvider) {
        $stateProvider
            .state(STATE.SALES.EDIT.ABSTRACT, {
                abstract: true,
                url: "/ventes/{id:[0-9]*}",
                params: {
                    liveMeta: undefined,
                    sites: undefined,
                    filter: undefined
                },
                resolve: {
                    // initUser is injected here to ensure that it's resolved before resolving acl permission
                    checkAcl: ["aclService", "initUser", aclService => aclService.isStateAllowed(auth.AclAccess.Sales)],
                    // Categories and checkAcl are injected here to ensure that they're resolved before resolving the sale
                    sale: [
                        "$log",
                        "$stateParams",
                        "saleEditModel",
                        "checkAcl",
                        "categories",
                        ($log: ng.ILogService, $stateParams: ng.ui.IStateParamsService, saleEditModel) => {
                            saleEditModel.setId($stateParams.id);

                            return new Promise((resolve, reject) =>
                                saleEditModel
                                    .fetchSale()
                                    .then(async () => {
                                        saleEditModel.getSaleTranslationStatus();
                                        saleEditModel.setSubscriptionsCount();
                                        saleEditModel.setOrFetchLiveResource($stateParams.liveMeta, $stateParams.sites);
                                        await Promise.all([
                                            saleEditModel.fetchLiveSale(),
                                            saleEditModel.getStripeContracts(),
                                            saleEditModel.getMonextContracts(),
                                            !saleEditModel.isPast
                                                ? saleEditModel.fetchItems().then(() => {
                                                      saleEditModel.checkIfEveryMandatoryFieldsAreFilled();
                                                      saleEditModel.checkIsPublishable();
                                                  })
                                                : undefined
                                        ]);
                                        return resolve(saleEditModel);
                                    })
                                    .catch(error => {
                                        $log.error(`Error while accessing sale content ${$stateParams.id}`, error);
                                        return reject({ redirectStateName: STATE.V2.ERROR_404.name });
                                    })
                            );
                        }
                    ],
                    iframeEvents: ["$log", "$rootScope", "$window", "saleEditModel", setIframeEvents]
                },
                views: {
                    "": {
                        template: require("./index.html"),
                        controller: SaleEditController,
                        controllerAs: "saleEditCtrl"
                    },
                    "information@thor.sale": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.INFO.url(resource?.id)}?oId=${organization?.id}`)
                        ]
                    },
                    "location@thor.sale": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.LOCATION.url(resource?.id)}?oId=${
                                    organization?.id
                                }`)
                        ]
                    },
                    "conditions@thor.sale": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.FEES_AND_CONDITIONS.url(resource?.id)}?oId=${
                                    organization?.id
                                }`)
                        ]
                    },
                    "shipping@thor.sale": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.SHIPPING.url(resource?.id)}?oId=${
                                    organization?.id
                                }`)
                        ]
                    },
                    "live@thor.sale": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            async ($scope, { organization }, saleEditModel) => {
                                $scope.url = `${STATE.V2.SALES.SALE.LIVE.url(saleEditModel.resource?.id)}?oId=${
                                    organization?.id
                                }`;
                                return await Promise.all([
                                    saleEditModel.fetchLiveSale(),
                                    saleEditModel.getStripeContracts()
                                ]);
                            }
                        ]
                    },
                    "items@thor.sale": {
                        templateProvider: [
                            "saleEditModel",
                            ({ resource }) =>
                                require(resource.isPast ? "../../shared/sale-edit/iframe.html" : "./items.html")
                        ],
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            ($scope, { organization }, saleEditModel) => {
                                $scope.sale = saleEditModel;
                                $scope.url = `${STATE.V2.SALES.SALE.ITEMS.url(saleEditModel.resource?.id)}?oId=${
                                    organization?.id
                                }`;
                            }
                        ]
                    },
                    "bids@thor.sale": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.BIDS.url(resource?.id)}?oId=${organization?.id}`)
                        ]
                    },
                    "subscriptions@thor.sale": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.SUBSCRIPTIONS.url(
                                    resource?.id,
                                    Enums.AuctionType.Live
                                )}?oId=${organization?.id}`)
                        ]
                    },
                    "payments@thor.sale": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.PAYMENTS.url(resource?.id)}?oId=${
                                    organization?.id
                                }`)
                        ]
                    },
                    "stats@thor.sale": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "saleEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.STATS.url(resource?.id)}?oId=${organization?.id}`)
                        ]
                    }
                }
            })
            .state(STATE.SALES.EDIT.INFORMATION, {
                url: ""
            })
            .state(STATE.SALES.EDIT.LOCATION, {
                url: "/address"
            })
            .state(STATE.SALES.EDIT.CONDITIONS, {
                url: "/conditions"
            })
            .state(STATE.SALES.EDIT.SHIPPING, {
                url: "/shipping"
            })
            .state(STATE.SALES.EDIT.LIVE, {
                url: "/live",
                onEnter: checkSaleLive
            })
            .state(STATE.SALES.EDIT.ITEMS, {
                url: "/lots"
            })
            .state(STATE.SALES.EDIT.BIDS, {
                url: "/ordres/:filter?"
            })
            .state(STATE.SALES.EDIT.SUBSCRIPTIONS, {
                url: "/subscriptions",
                onEnter: checkSaleLive
            })
            .state(STATE.SALES.EDIT.PAYMENTS, {
                url: "/payments"
            })
            .state(STATE.SALES.EDIT.STATS, {
                url: "/stats"
            });
    }
}
